// Every full date shown to a user anywhere in this app must read as
// "DD-MM-YYYY" (client instruction, 2026-08-07, applied site-wide —
// supersedes the earlier "04 Aug 2026" word-month convention some pages
// had adopted). This is a DISPLAY-only helper — stored/submitted values
// (API payloads, "YYYY-MM-DD" form fields, query params) are untouched.
//
// Parses "YYYY-MM-DD"/ISO strings by direct digit-extraction rather than
// `new Date(...).toLocaleDateString()` — the same locale/timezone-parsing
// hazard this codebase already avoids in formatPeriodMonth.js. A plain
// "YYYY-MM-DD" string passed to `new Date()` is parsed as UTC midnight,
// which `toLocaleDateString()` then renders in the browser's local
// timezone — one day off for anyone west of UTC. Slicing the leading
// "YYYY-MM-DD" out of the raw string sidesteps that entirely.
export function formatDate(value) {
  if (!value) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) {
    // Not a "YYYY-MM-DD"-prefixed string (e.g. already a Date object) —
    // fall back to reading local calendar fields off a parsed Date.
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  }
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

// Every timestamp column in the backend now lives on a UTC-pinned DB
// session (client ask, 2026-08-19: "keep UTC in the tables... show IST
// on the frontend" — see jpc-supplier-backend/db.js's
// pool.on('connection', ...) and sql/procurement/
// 094_utc_datetime_backfill.sql). Before that fix, the DB server's own
// wall clock WAS already Asia/Kolkata, so treating the raw string as
// "already correct, just reformat the digits" (the old approach here)
// happened to work by coincidence. Now the raw string is genuine UTC,
// so it must be explicitly converted to IST before display — done via
// Intl.DateTimeFormat with timeZone: 'Asia/Kolkata' (real IANA tz data,
// not a hand-rolled +5:30 offset) rather than relying on the viewer's
// own browser timezone, since the client asked for IST specifically,
// not "whichever timezone the visitor happens to be in."
//
// parseAsUtc marks a bare "YYYY-MM-DD HH:MM:SS" string (no trailing
// 'Z'/offset — dateStrings:true on the backend never adds one) as UTC
// before handing it to Date, exactly the same hazard formatPeriodMonth.js
// and the old formatDate() above already guard against for date-only
// values, just for the full timestamp here.
export function parseAsUtc(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasZone ? s : `${s.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const IST_TIME_ZONE = "Asia/Kolkata";

// formatToParts (not toLocaleString) so callers get individual
// day/month/year/hour/minute pieces to assemble in whichever order they
// need — DD-MM-YYYY here, but the pieces are IST either way.
function istParts(d, hour12) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12,
  }).formatToParts(d);
  const map = {};
  parts.forEach((p) => { map[p.type] = p.value; });
  return map;
}

// A timestamp's IST calendar date as "YYYY-MM-DD" — for comparing a
// genuine timestamp column (created_at/submitted_at/etc.) against a
// plain date filter/picker value (which is itself already a calendar
// date, no time-of-day), WITHOUT the bug of just slicing the leading
// 10 characters off the raw UTC string. A UTC timestamp between 18:30
// and 23:59:59 falls on the NEXT calendar day in IST — e.g.
// "2026-08-19 19:00:00" UTC is "2026-08-20" in IST, not "2026-08-19".
export function toISTDateKey(value) {
  const d = parseAsUtc(value);
  if (!d) return null;
  const p = istParts(d, false);
  return `${p.year}-${p.month}-${p.day}`;
}

// Same "DD-MM-YYYY" convention, plus a trailing "hh:MM AM/PM" for
// timestamp fields (created_at/submitted_at/etc.) — converts the DB's
// raw UTC string to IST before formatting (see note above). Was
// 24-hour ("HH:MM") until 2026-08-21, when the client asked for every
// date-time across the app to read in AM/PM instead — since every
// call site already goes through this one shared helper, switching its
// output here (rather than formatDateTime12h's own, separately-named
// function) fixed every page at once.
export function formatDateTime(value) {
  const d = parseAsUtc(value);
  if (!d) return "—";
  return formatIST12h(d);
}

function formatIST12h(d) {
  const p = istParts(d, true);
  const datePart = `${p.day}-${p.month}-${p.year}`;
  const hour = p.hour === "24" ? "12" : p.hour; // Intl edge case: some engines render midnight as "24" even with hour12
  return `${datePart}, ${hour}:${p.minute} ${p.dayPeriod.toUpperCase()}`;
}

// Kept as an alias of formatDateTime (identical output, both AM/PM
// since 2026-08-21) purely so the 2 existing call sites importing this
// name specifically don't need to change.
export function formatDateTime12h(value) {
  const d = parseAsUtc(value);
  if (!d) return "—";
  return formatIST12h(d);
}
